/**
 * useAEAE Hook - Integración AEAE para MBI/OBI
 * Proporciona análisis emocional y adaptación de UI
 */
import { useState, useCallback, useRef, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Tonos de UI disponibles
export const UI_TONES = {
  NEUTRAL: 'neutral',
  CALM: 'calm',
  ENERGETIC: 'energetic',
  EMPATHETIC: 'empathetic',
  CONFUSED: 'confused'
};

// Velocidades de animación
export const ANIMATION_SPEEDS = {
  SLOW: 'slow',
  NORMAL: 'normal',
  FAST: 'fast'
};

// Variantes de mensaje
export const MESSAGE_VARIANTS = {
  STANDARD: 'standard',
  CALM: 'calm',
  ENERGETIC: 'energetic',
  EMPATHETIC: 'empathetic'
};

// Duración de animaciones según velocidad (ms)
export const ANIMATION_DURATIONS = {
  slow: {
    transition: 500,
    fadeIn: 600,
    slideIn: 700,
    pulse: 3000
  },
  normal: {
    transition: 300,
    fadeIn: 400,
    slideIn: 400,
    pulse: 2000
  },
  fast: {
    transition: 150,
    fadeIn: 200,
    slideIn: 250,
    pulse: 1500
  }
};

/**
 * Hook principal para integración AEAE
 */
export const useAEAE = () => {
  // Estado emocional actual
  const [emotionalState, setEmotionalState] = useState({
    uiTone: UI_TONES.NEUTRAL,
    animationSpeed: ANIMATION_SPEEDS.NORMAL,
    messageVariant: MESSAGE_VARIANTS.STANDARD,
    confidence: 0.5,
    isLoading: false,
    error: null
  });

  // Referencia para tracking de tiempo en paso
  const stepStartTime = useRef(Date.now());
  const previousEmotion = useRef(null);

  // Reset del timer cuando cambia el paso
  const resetStepTimer = useCallback(() => {
    stepStartTime.current = Date.now();
  }, []);

  // Calcular tiempo en paso actual
  const getTimeOnStep = useCallback(() => {
    return Math.floor((Date.now() - stepStartTime.current) / 1000);
  }, []);

  /**
   * Analiza un mensaje de texto para detectar emoción
   */
  const analyzeMessage = useCallback(async (message, conversationId = null) => {
    if (!message || message.trim().length < 2) {
      return {
        uiTone: UI_TONES.NEUTRAL,
        animationSpeed: ANIMATION_SPEEDS.NORMAL,
        messageVariant: MESSAGE_VARIANTS.STANDARD,
        confidence: 0.9
      };
    }

    setEmotionalState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${API_URL}/api/aeae/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          conversation_id: conversationId
        })
      });

      if (!response.ok) throw new Error('AEAE analysis failed');

      const data = await response.json();
      
      const newState = {
        uiTone: data.ui_tone || UI_TONES.NEUTRAL,
        animationSpeed: data.animation_speed || ANIMATION_SPEEDS.NORMAL,
        messageVariant: getMessageVariantFromTone(data.ui_tone),
        confidence: data.confidence || 0.5,
        isLoading: false,
        error: null
      };

      previousEmotion.current = data.emotion;
      setEmotionalState(newState);
      return newState;

    } catch (error) {
      console.warn('AEAE analysis error, using fallback:', error);
      // Fallback seguro
      const fallback = {
        uiTone: UI_TONES.NEUTRAL,
        animationSpeed: ANIMATION_SPEEDS.NORMAL,
        messageVariant: MESSAGE_VARIANTS.STANDARD,
        confidence: 0.5,
        isLoading: false,
        error: null
      };
      setEmotionalState(fallback);
      return fallback;
    }
  }, []);

  /**
   * Análisis rápido basado en comportamiento (para MBI/OBI)
   */
  const quickAnalyze = useCallback(async (stepContext = null) => {
    setEmotionalState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${API_URL}/api/aeae/quick-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          time_on_step: getTimeOnStep(),
          step_context: stepContext,
          previous_emotion: previousEmotion.current
        })
      });

      if (!response.ok) throw new Error('AEAE quick analysis failed');

      const data = await response.json();
      
      const newState = {
        uiTone: data.ui_tone || UI_TONES.NEUTRAL,
        animationSpeed: data.animation_speed || ANIMATION_SPEEDS.NORMAL,
        messageVariant: data.message_variant || MESSAGE_VARIANTS.STANDARD,
        confidence: data.confidence || 0.5,
        isLoading: false,
        error: null
      };

      setEmotionalState(newState);
      return newState;

    } catch (error) {
      console.warn('AEAE quick analysis error, using fallback:', error);
      // Fallback seguro
      const fallback = {
        uiTone: UI_TONES.NEUTRAL,
        animationSpeed: ANIMATION_SPEEDS.NORMAL,
        messageVariant: MESSAGE_VARIANTS.STANDARD,
        confidence: 0.5,
        isLoading: false,
        error: null
      };
      setEmotionalState(fallback);
      return fallback;
    }
  }, [getTimeOnStep]);

  /**
   * Establece tono manualmente (para casos donde no hay análisis)
   */
  const setTone = useCallback((tone) => {
    if (Object.values(UI_TONES).includes(tone)) {
      setEmotionalState(prev => ({
        ...prev,
        uiTone: tone,
        messageVariant: getMessageVariantFromTone(tone)
      }));
    }
  }, []);

  /**
   * Obtiene duraciones de animación según estado actual
   */
  const getAnimationDurations = useCallback(() => {
    return ANIMATION_DURATIONS[emotionalState.animationSpeed] || ANIMATION_DURATIONS.normal;
  }, [emotionalState.animationSpeed]);

  /**
   * Genera clases CSS para animaciones según estado emocional
   */
  const getAnimationClasses = useCallback(() => {
    const { animationSpeed, uiTone } = emotionalState;
    const classes = [];

    // Duración de transición
    switch (animationSpeed) {
      case ANIMATION_SPEEDS.SLOW:
        classes.push('duration-500');
        break;
      case ANIMATION_SPEEDS.FAST:
        classes.push('duration-150');
        break;
      default:
        classes.push('duration-300');
    }

    // Easing según tono
    switch (uiTone) {
      case UI_TONES.CALM:
      case UI_TONES.EMPATHETIC:
        classes.push('ease-out');
        break;
      case UI_TONES.ENERGETIC:
        classes.push('ease-in-out');
        break;
      default:
        classes.push('ease-out');
    }

    return classes.join(' ');
  }, [emotionalState]);

  return {
    // Estado
    emotionalState,
    uiTone: emotionalState.uiTone,
    animationSpeed: emotionalState.animationSpeed,
    messageVariant: emotionalState.messageVariant,
    confidence: emotionalState.confidence,
    isLoading: emotionalState.isLoading,

    // Métodos
    analyzeMessage,
    quickAnalyze,
    setTone,
    resetStepTimer,
    getTimeOnStep,
    getAnimationDurations,
    getAnimationClasses,

    // Constantes
    UI_TONES,
    ANIMATION_SPEEDS,
    MESSAGE_VARIANTS
  };
};

/**
 * Mapea tono de UI a variante de mensaje
 */
function getMessageVariantFromTone(tone) {
  const mapping = {
    [UI_TONES.NEUTRAL]: MESSAGE_VARIANTS.STANDARD,
    [UI_TONES.CALM]: MESSAGE_VARIANTS.CALM,
    [UI_TONES.ENERGETIC]: MESSAGE_VARIANTS.ENERGETIC,
    [UI_TONES.EMPATHETIC]: MESSAGE_VARIANTS.EMPATHETIC,
    [UI_TONES.CONFUSED]: MESSAGE_VARIANTS.CALM
  };
  return mapping[tone] || MESSAGE_VARIANTS.STANDARD;
}

export default useAEAE;
